import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useWorkshiftAPI } from '../apis/workshift-api';
import { useErrorHandler } from '../hooks/use-error-handler';
import { useStorage } from '../hooks/use-storage';
import { useSubmitOrderModal } from '../screens/order/submit-order-modal';
import { useUserManager } from '../utils/manager/user-manager';
import { useAuthWorkflow } from './auth-workflow';
import { useProductSelectionWorkflow } from './product-selection-workflow';
import { useSpotWorkflow } from './spot-workflow';
import { useAuthRejectedModal } from '../screens/order/auth-rejected-modal';

function useCartWorkflow() {
  const { checkCurrentUser } = useUserManager();
  const { prepareProductSelectionWorkflow, showProductSelectionWorkflow } =
    useProductSelectionWorkflow();
  const { prepareSpotWorkflow, showSpotWorkflow } = useSpotWorkflow();
  const { prepareAuthWorkflow, showAuthWorkflow } = useAuthWorkflow();
  const { showAuthRejectedModal } = useAuthRejectedModal();
  const { prepareSubmitOrderModal, showSubmitOrderModal } =
    useSubmitOrderModal();
  const { loadCurrentWorkshiftFromRemote } = useWorkshiftAPI();
  const { throwNewValidationError } = useErrorHandler();
  const { setUser, userRef, spotRef } = useStorage();
  const { t } = useTranslation();

  function shouldSubmitOrder() {
    return userRef.current != null && spotRef.current != null;
  }

  async function prepare({ category, item }) {
    await prepareProductSelectionWorkflow({ category, item });
  }

  async function show({ prepareOnPreConfirm } = {}) {
    let result;

    result = await showProductSelectionWorkflow({
      prepareOnPreConfirm: async ({ newOrder }) => {
        await checkCurrentUser(() => setUser(null));

        const response = await loadCurrentWorkshiftFromRemote();
        const workshift = response.data;

        if (workshift == null || workshift.finished_at != null) {
          throwNewValidationError(t('workshift-closed'));
        }

        await prepareAuthWorkflowIfNecessary();
        await prepareSpotWorkflowIfNecessary();
        await prepareSubmitOrderModalIfNecessary({ newOrder });
        // await submitOrderIfPossible(newOrder);

        await prepareOnPreConfirm?.call(this);
      },
    });

    if (result.isConfirmed) {
      let repeat;

      do {
        repeat = false;

        if (userRef.current == null) {
          // eslint-disable-next-line no-await-in-loop
          const authResult = await showAuthWorkflow({
            showName: true,
            // forceShowAuthRejectedModal: true,

            // eslint-disable-next-line no-loop-func
            prepareOnPreConfirm: async () => {
              await prepareSpotWorkflowIfNecessary();
              // await submitOrderIfPossible(result.newOrder);
              await prepareSubmitOrderModalIfNecessary({
                newOrder: result.newOrder,
              });
            },
          });

          result = _.merge(result, authResult);
        }

        if (userRef.current == null) {
          // eslint-disable-next-line no-await-in-loop
          await showAuthRejectedModal();
        }

        if (spotRef.current == null && !result.isDismissed) {
          // eslint-disable-next-line no-await-in-loop
          const spotResult = await showSpotWorkflow({
            // eslint-disable-next-line no-loop-func
            prepareOnPreConfirm: async () => {
              // await submitOrderIfPossible(result.newOrder);
              await prepareSubmitOrderModalIfNecessary({
                newOrder: result.newOrder,
              });
            },
          });
          result = _.merge(result, spotResult);
        }

        if (shouldSubmitOrder()) {
          // eslint-disable-next-line no-await-in-loop
          const submitResult = await showSubmitOrderModal();

          result = _.merge(result, submitResult);

          result.isConfirmed = true;
          result.isDismissed = false;
          result.goToBill =
            submitResult.isConfirmed && _.isEmpty(submitResult.suggestion);
        }

        if (
          !result.isDismissed &&
          (userRef.current == null || spotRef.current == null)
        ) {
          repeat = true;
        }
      } while (repeat);
    }

    if (result.isDismissed) {
      result.newOrder = null;
    }

    return result;
  }

  async function prepareAuthWorkflowIfNecessary() {
    if (userRef.current == null) {
      await prepareAuthWorkflow();
    }
  }

  async function prepareSpotWorkflowIfNecessary() {
    if (spotRef.current == null) {
      await prepareSpotWorkflow();
    }
  }

  async function prepareSubmitOrderModalIfNecessary({ newOrder }) {
    if (shouldSubmitOrder()) {
      await prepareSubmitOrderModal({ newOrder });
    }
  }

  return {
    prepareCartWorkflow: prepare,
    showCartWorkflow: show,
  };
}

export { useCartWorkflow };
